/**
 * Overview URL state: the shared Focus scope and nothing else. Overview opens
 * on the user's Focus app like every screen, with All apps (the portfolio)
 * one click away in the app switcher, the data-anchored date chip (Last
 * 7 / 14 / 28 days) and the Filters panel without the Performance-only
 * controls (Status, thresholds, 24-hour activity), which the Overview
 * endpoints do not read.
 *
 * apps/web's Overview wrote no URL (parity §3 "Scope and URL"): the scope now
 * lives in the URL, so a link to the Overview — and every link out of it —
 * carries it.
 */
import { defineFocusConfig, parseFocusSearch, type FocusControls, type FocusSearch } from '@/features/focus';
import { defineSearch, type RawSearch } from '@/router/search';

const CONTROLS = {
  allApps: true,
  dates: true,
  filters: { status: false, thresholds: false, activity: false },
} as const satisfies FocusControls;

export type OverviewSearch = FocusSearch;

export const parseOverviewSearch = (raw: RawSearch): OverviewSearch => parseFocusSearch(raw, CONTROLS);

export const overviewSearch = defineSearch(parseOverviewSearch);

export const OVERVIEW_FOCUS = defineFocusConfig({
  ...CONTROLS,
  parse: parseOverviewSearch,
  appSwitcher: {
    evidenceFor: 'statuses and insights',
    hint: 'Click an app to look at it alone, tick several to combine them, or choose All apps for the portfolio.',
  },
});
